/**
 * tr-cart — moteur panier unique de Traction.
 *
 * Un seul objet parle à l'Ajax Cart API : `window.Traction.cart` (TrCartEngine).
 * Les hôtes `<tr-cart-drawer>` (tiroir) et `<tr-cart>` (page) déclarent des
 * zones à rafraîchir (`data-cart-refresh` + `data-ref`) ; le moteur demande
 * leurs sections au Section Rendering API (≤ 5, null-safe) et remplace le
 * HTML par DOMParser.
 *
 * Événements document :
 *   écoute  tr:cart-add     { items, cart, source, sections? } — émis par tr-product-form,
 *                           quick-add (product-form.js), cart-upsell. Si `sections`
 *                           est présent, le HTML a déjà été rafraîchi (émis par le moteur).
 *   écoute  tr:cart-error   { status, cart?, engine? } — 422 externe = succès partiel : relecture.
 *   écoute  tr:cart-open / tr:cart-close
 *   écoute  tr:cart-update  { updates?, note?, attributes?, source?, render? }
 *   émet    tr:cart-updated { cart, source, sections }
 *   émet    tr:cart-error   { message, status, source, engine: true }
 *   émet    shopify:cart:lines-update { cart, source } (compat apps)
 *
 * Relais : `Shopify.actions.openCart / closeCart / updateCart / getCart`
 * ajoutés s'ils n'existent pas déjà (jamais écrasés).
 *
 * Zéro dépendance. Module ES. Custom elements idempotents. Références par `data-ref`.
 */

/* ---------- monnaie & événements (modules partagés W-B) ---------- */

import { formatMoney } from './tr-money.js';
import { TR_EVENTS } from './tr-events.js';

export { formatMoney };

/* ---------- helpers ---------- */

function route(name, fallback) {
  const value = window.Traction?.routes?.[name] ?? window.routes?.[name];
  if (typeof value === 'string' && value !== '') return value;

  const root = window.Shopify?.routes?.root ?? '/';
  return `${root}${fallback}`.replace('//', '/');
}

function jsonRoute(name, fallback) {
  const value = route(name, fallback);
  return value.endsWith('.js') ? value : `${value}.js`;
}

function dispatch(name, detail = {}) {
  document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
}

function cartPathname() {
  try {
    return new URL(route('cart_url', 'cart'), window.location.origin).pathname.replace(/\/$/, '');
  } catch {
    return '/cart';
  }
}

const HOST_SELECTOR = 'tr-cart-drawer, tr-cart';
const BUBBLE_SECTION = 'cart-icon-bubble';
const MAX_SECTIONS = 5;

/* ---------- règles de quantité (quantity_rule : min / pas / max) ---------- */

/**
 * Règle d'un champ quantité. `data-min` porte `variant.quantity_rule.min`
 * (l'attribut `min` du champ vaut 0 : décrémenter jusqu'à 0 supprime la ligne).
 * @param {HTMLInputElement} field
 */
function quantityRule(field) {
  const step = Math.max(1, Math.floor(Number(field.step) || 1));
  const min = Math.max(step, Math.floor(Number(field.dataset.min) || 1));
  const rawMax = field.max === '' || field.max === undefined ? NaN : Number(field.max);

  return { min, step, max: Number.isNaN(rawMax) ? Infinity : rawMax };
}

/**
 * Cale une quantité sur la règle du produit (B2B : minimum 6, par 6…).
 * 0 reste 0 (suppression). Évite un aller-retour 422 sur une saisie invalide.
 * @param {HTMLInputElement} field
 * @param {number} raw
 */
function normalizeQuantity(field, raw) {
  const value = Math.max(0, Math.floor(Number(raw) || 0));
  if (value === 0) return 0;

  const { min, step, max } = quantityRule(field);
  if (value <= min) return Math.min(min, max === Infinity ? min : max);

  const wanted = min + Math.ceil((value - min) / step) * step;
  if (wanted <= max) return wanted;

  /* Plafond hors grille : on redescend au dernier palier valide. */
  return min + Math.max(0, Math.floor((max - min) / step)) * step;
}

/* ---------- moteur ---------- */

/**
 * Message lisible d'une réponse d'erreur Ajax. Les erreurs de carte cadeau
 * (bénéficiaire) arrivent avec `description` en objet `{ email: ['n’est pas valide'] }` ;
 * `message` porte alors la phrase complète (« Validation échouée : E-mail n’est pas valide »).
 */
function errorText(data, fallback) {
  if (typeof data.description === 'string' && data.description !== '') return data.description;
  if (typeof data.message === 'string' && data.message !== '') return data.message;

  const detail = data.errors ?? data.description;
  if (detail !== null && typeof detail === 'object') {
    const text = Object.values(detail).flat().join(' ');
    if (text !== '') return text;
  }

  return data.message ?? fallback;
}

class TrCartEngine {
  #queue = Promise.resolve();

  constructor() {
    document.addEventListener(TR_EVENTS.CART_ADD, this.#onExternalAdd);
    document.addEventListener(TR_EVENTS.CART_ERROR, this.#onExternalError);
    document.addEventListener(TR_EVENTS.CART_UPDATE, this.#onExternalUpdate);

    /* 04·§5 : le bouton de paiement n'avait aucun retour visuel. Délégation au
       document : le bouton est un noeud neuf après chaque re-rendu de zone. */
    document.addEventListener('click', this.#onCheckoutClick);
    window.addEventListener('pageshow', this.#onPageShow);

    this.#relayShopifyActions();
  }

  /* ----- API publique ----- */

  /** Hôtes présents sur la page (tiroir et/ou page panier). */
  get hosts() {
    return Array.from(document.querySelectorAll(HOST_SELECTOR));
  }

  /** Sections à demander au Section Rendering (≤ 5). */
  get sectionIds() {
    const ids = [];

    for (const host of this.hosts) {
      const id = host.dataset.sectionId;
      if (id !== undefined && id !== '' && ids.includes(id) === false) ids.push(id);
    }

    if (document.getElementById(BUBBLE_SECTION) !== null) ids.push(BUBBLE_SECTION);

    return ids.slice(0, MAX_SECTIONS);
  }

  /**
   * Ajoute des articles (un seul appel `items[]`).
   * @param {Array<{id:number, quantity:number, properties?:object, selling_plan?:number}>} items
   * @param {{source?:string, open?:boolean}} [options]
   */
  add(items, { source = 'engine', open = false } = {}) {
    const list = Array.isArray(items) ? items : [items];
    if (list.length === 0) return Promise.resolve(null);

    return this.#enqueue(async () => {
      const result = await this.#post(jsonRoute('cart_add_url', 'cart/add'), { items: list }, { source });

      if (result.ok === false) {
        /* 422 : succès partiel possible (stock) → relecture. */
        if (result.status === 422) await this.#refreshSilently(source);
        return result;
      }

      const cart = await this.#applyAndFetch(result.payload, source, 'added');

      dispatch(TR_EVENTS.CART_ADD, { items: list, cart, source, sections: result.payload.sections ?? {} });
      if (open) this.open();

      return { ...result, cart };
    });
  }

  /**
   * Change la quantité d'une ligne (clé de ligne, jamais l'index). 0 = suppression.
   * @param {string} key
   * @param {number} quantity
   */
  change(key, quantity, { source = 'engine' } = {}) {
    const next = Math.max(0, Number(quantity) || 0);

    return this.#enqueue(async () => {
      this.#setLineBusy(key, true);

      const result = await this.#post(
        jsonRoute('cart_change_url', 'cart/change'),
        { id: key, quantity: next },
        { source, lineKey: key }
      );

      if (result.ok === false) {
        /* Stock insuffisant : relecture (quantité rétablie) puis message sur la ligne. */
        await this.#refreshSilently(source);
        this.#setLineError(key, result.message);
        this.#setLineBusy(key, false);
        return result;
      }

      const cart = await this.#applyAndFetch(result.payload, source, next === 0 ? 'removed' : 'updated');
      this.#setLineBusy(key, false);

      return { ...result, cart };
    });
  }

  /**
   * Mise à jour groupée : `{ updates, note, attributes }`.
   * `render: false` = pas de re-rendu des sections (ex. note de commande).
   */
  update(payload, { source = 'engine', render = true } = {}) {
    return this.#enqueue(async () => {
      const result = await this.#post(jsonRoute('cart_update_url', 'cart/update'), { ...payload }, { source, render });

      if (result.ok === false) {
        if (render) await this.#refreshSilently(source);
        return result;
      }

      const cart = await this.#applyAndFetch(result.payload, source, render ? 'updated' : null, render);
      return { ...result, cart };
    });
  }

  /** Lit le panier (`/cart.js`). `null` en cas d'échec. */
  async fetch() {
    try {
      const response = await window.fetch(jsonRoute('cart_url', 'cart'), {
        headers: { Accept: 'application/json' },
      });
      if (response.ok === false) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  /** Re-rend les sections depuis le serveur (GET) et émet tr:cart-updated. */
  refresh({ source = 'engine', cart = null } = {}) {
    for (const host of this.hosts) this.#setHostBusy(host, true);

    return this.#enqueue(async () => {
      const sections = await this.#fetchSections();
      this.#applySections(sections);

      const fresh = cart ?? (await this.fetch());
      this.#notify(fresh, source, sections);

      return { ok: fresh !== null, cart: fresh, sections };
    });
  }

  open() {
    for (const host of this.hosts) {
      if (typeof host.open === 'function') host.open();
    }
  }

  close() {
    for (const host of this.hosts) {
      if (typeof host.close === 'function') host.close();
    }
  }

  /* ----- réactions aux événements externes ----- */

  #onExternalAdd = (event) => {
    /* Déjà rendu par le moteur lui-même. */
    if (event.detail?.sections !== undefined) return;

    const source = event.detail?.source ?? 'external';
    const cart = event.detail?.cart ?? null;

    this.#announce('added');
    this.refresh({ source, cart });
  };

  #onExternalError = (event) => {
    if (event.detail?.engine === true) return;
    if (Number(event.detail?.status) !== 422) return;

    this.refresh({ source: event.detail?.source ?? 'external', cart: event.detail?.cart ?? null });
  };

  /**
   * Soumission vers le paiement : état d'attente VISUEL uniquement.
   * Jamais `disabled` — cela annulerait la soumission native du formulaire.
   */
  #onCheckoutClick = (event) => {
    if (event.defaultPrevented) return;

    const button = event.target?.closest?.('[data-ref="checkoutButton"]');
    if (button === null || button === undefined || button.disabled === true) return;

    button.setAttribute('aria-busy', 'true');
  };

  /** Retour arrière (bfcache) : le bouton doit redevenir cliquable visuellement. */
  #onPageShow = () => {
    for (const button of document.querySelectorAll('[data-ref="checkoutButton"][aria-busy="true"]')) {
      button.setAttribute('aria-busy', 'false');
    }
  };

  #onExternalUpdate = (event) => {
    const detail = event.detail ?? {};
    const payload = {};

    if (detail.updates !== undefined) payload.updates = detail.updates;
    if (detail.note !== undefined) payload.note = detail.note;
    if (detail.attributes !== undefined) payload.attributes = detail.attributes;

    if (Object.keys(payload).length === 0) {
      this.refresh({ source: detail.source ?? 'external' });
      return;
    }

    this.update(payload, { source: detail.source ?? 'external', render: detail.render !== false });
  };

  /* ----- interne : requêtes ----- */

  async #post(url, body, { source, lineKey = null, render = true }) {
    const sections = render ? this.sectionIds : [];
    const payload = { ...body };

    if (sections.length > 0) {
      payload.sections = sections.join(',');
      payload.sections_url = window.location.pathname;
    }

    if (render) {
      for (const host of this.hosts) this.#setHostBusy(host, true);
      this.#clearHostErrors();
    }

    try {
      const response = await window.fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      const status = Number(data.status) || response.status;
      const failed = response.ok === false || data.status !== undefined;

      if (failed) {
        const message = errorText(data, this.#string('error'));

        for (const host of this.hosts) this.#setHostBusy(host, false);
        if (lineKey === null) this.#showHostError(message);
        this.#announce('error', message);

        dispatch(TR_EVENTS.CART_ERROR, { message, status, source, cart: null, engine: true });

        return { ok: false, status, message, payload: data };
      }

      return { ok: true, status, message: '', payload: data };
    } catch (error) {
      const message = this.#string('network');

      for (const host of this.hosts) this.#setHostBusy(host, false);
      this.#showHostError(message);
      this.#announce('error', message);

      dispatch(TR_EVENTS.CART_ERROR, { message, status: 0, source, cart: null, engine: true, error: String(error) });

      return { ok: false, status: 0, message, payload: {} };
    }
  }

  async #fetchSections() {
    const ids = this.sectionIds;
    if (ids.length === 0) return {};

    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('sections', ids.join(','));

    try {
      const response = await window.fetch(url.toString(), { headers: { Accept: 'application/json' } });
      if (response.ok === false) return {};
      return await response.json();
    } catch {
      return {};
    }
  }

  async #refreshSilently(source) {
    const sections = await this.#fetchSections();
    this.#applySections(sections);

    const cart = await this.fetch();
    this.#notify(cart, source, sections);
  }

  /**
   * Applique les sections renvoyées par l'Ajax API puis expose le panier JSON.
   * change/update renvoient le panier ; add renvoie les lignes ajoutées → relecture.
   */
  async #applyAndFetch(payload, source, announceKey, render = true) {
    const sections = payload?.sections ?? {};
    if (render) this.#applySections(sections);

    const cart = payload?.token !== undefined ? payload : await this.fetch();

    if (announceKey !== null) this.#announce(announceKey);
    this.#notify(cart, source, sections);

    return cart;
  }

  /* ----- interne : DOM ----- */

  #applySections(sections) {
    const focusMemo = this.#rememberFocus();

    for (const host of this.hosts) {
      const html = sections?.[host.dataset.sectionId];

      if (typeof html !== 'string') {
        this.#setHostBusy(host, false);
        continue;
      }

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector(HOST_SELECTOR);

      if (fresh === null) {
        this.#setHostBusy(host, false);
        continue;
      }

      for (const zone of host.querySelectorAll('[data-cart-refresh]')) {
        const ref = zone.dataset.ref;
        if (ref === undefined) continue;

        const next = fresh.querySelector(`[data-cart-refresh][data-ref="${ref}"]`);
        if (next === null) continue;

        zone.replaceChildren(...Array.from(next.childNodes));
      }

      host.dataset.itemCount = fresh.dataset.itemCount ?? host.dataset.itemCount ?? '0';
      host.classList.toggle('is-empty', fresh.classList.contains('is-empty'));

      this.#setHostBusy(host, false);
    }

    const bubbleHtml = sections?.[BUBBLE_SECTION];
    const bubble = document.getElementById(BUBBLE_SECTION);

    if (typeof bubbleHtml === 'string' && bubble !== null) {
      const parsed = new DOMParser().parseFromString(bubbleHtml, 'text/html');
      const inner = parsed.querySelector('.shopify-section') ?? parsed.body;
      bubble.replaceChildren(...Array.from(inner.childNodes));
    }

    this.#restoreFocus(focusMemo);
  }

  #rememberFocus() {
    const active = document.activeElement;
    if (active === null || active === document.body) return null;

    const host = active.closest(HOST_SELECTOR);
    if (host === null) return null;

    return {
      host,
      ref: active.dataset.ref ?? null,
      lineKey: active.closest('[data-line-key]')?.dataset.lineKey ?? null,
    };
  }

  #restoreFocus(memo) {
    if (memo === null || memo.ref === null) return;
    if (document.activeElement !== document.body && memo.host.contains(document.activeElement)) return;

    const { host, ref, lineKey } = memo;
    const scope = lineKey !== null ? host.querySelector(`[data-line-key="${CSS.escape(lineKey)}"]`) : null;

    const target =
      scope?.querySelector(`[data-ref="${ref}"]`) ??
      host.querySelector(`[data-ref="${ref}"]`) ??
      host.querySelector('[data-ref="cartFocus"]');

    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  }

  #setHostBusy(host, busy) {
    host.classList.toggle('is-loading', busy);
    host.setAttribute('aria-busy', String(busy));
  }

  #setLineBusy(key, busy) {
    for (const line of this.#lines(key)) {
      line.classList.toggle('is-loading', busy);
      line.setAttribute('aria-busy', String(busy));
    }
  }

  #setLineError(key, message) {
    for (const line of this.#lines(key)) {
      const target = line.querySelector('[data-ref="lineError"]');
      if (target === null) continue;

      target.textContent = message ?? '';
      target.hidden = message === undefined || message === '';
    }
  }

  #lines(key) {
    if (typeof key !== 'string' || key === '') return [];
    return document.querySelectorAll(`[data-line-key="${CSS.escape(key)}"]`);
  }

  #showHostError(message) {
    for (const host of this.hosts) {
      const target = host.querySelector('[data-ref="cartError"]');
      if (target === null) continue;

      target.textContent = message;
      target.hidden = false;
    }
  }

  #clearHostErrors() {
    for (const host of this.hosts) {
      const target = host.querySelector('[data-ref="cartError"]');
      if (target === null) continue;

      target.textContent = '';
      target.hidden = true;
    }
  }

  /** Région aria-live de chaque hôte (`data-ref="cartLive"`). */
  #announce(key, message) {
    for (const host of this.hosts) {
      const live = host.querySelector('[data-ref="cartLive"]');
      if (live === null) continue;

      const text = message ?? this.#string(key, host);
      live.textContent = '';
      /* Relecture forcée même si le texte est identique. */
      window.requestAnimationFrame(() => {
        live.textContent = text;
      });
    }
  }

  #string(key, host = this.hosts[0]) {
    const map = {
      added: host?.dataset.stringAdded,
      updated: host?.dataset.stringUpdated,
      removed: host?.dataset.stringRemoved,
      error: host?.dataset.stringError ?? window.Traction?.strings?.cartError,
      network: host?.dataset.stringNetwork ?? window.Traction?.strings?.networkError,
    };

    return map[key] ?? '';
  }

  /* ----- interne : diffusion ----- */

  #notify(cart, source, sections) {
    this.#syncBubbleCount(cart, sections);
    dispatch(TR_EVENTS.CART_UPDATED, { cart, source, sections });
    dispatch('shopify:cart:lines-update', { cart, source });
  }

  /**
   * Re-comptage de sécurité de la bulle (11·R12) : si le Section Rendering n'a
   * pas re-rendu `cart-icon-bubble` (> 5 sections, app tierce…), le compteur
   * est réaligné sur le JSON panier.
   */
  #syncBubbleCount(cart, sections) {
    if (cart === null || typeof cart !== 'object') return;
    if (typeof sections?.[BUBBLE_SECTION] === 'string') return;

    const bubble = document.getElementById(BUBBLE_SECTION)?.querySelector('.cart-count-bubble span[aria-hidden]');
    if (bubble === null || bubble === undefined) return;

    const count = Number(cart.item_count) || 0;
    bubble.textContent = count < 100 ? String(count) : '';
  }

  #relayShopifyActions() {
    if (window.Shopify === undefined) return;

    window.Shopify.actions = window.Shopify.actions ?? {};
    const actions = window.Shopify.actions;

    if (typeof actions.openCart !== 'function') actions.openCart = () => this.open();
    if (typeof actions.closeCart !== 'function') actions.closeCart = () => this.close();
    if (typeof actions.getCart !== 'function') actions.getCart = () => this.fetch();
    if (typeof actions.updateCart !== 'function') {
      actions.updateCart = (payload) =>
        payload === undefined
          ? this.refresh({ source: 'shopify-actions' })
          : this.update(payload, { source: 'shopify-actions' });
    }
  }

  /* ----- interne : file d'attente (une mutation à la fois) ----- */

  #enqueue(task) {
    const run = this.#runAfter(this.#queue, task);
    this.#queue = run.catch(() => undefined);
    return run;
  }

  async #runAfter(previous, task) {
    try {
      await previous;
    } catch {
      /* l'échec précédent a déjà été traité */
    }

    return task();
  }
}

/* ---------- singleton ---------- */

window.Traction = window.Traction ?? {};
if (typeof window.Traction.formatMoney !== 'function') window.Traction.formatMoney = formatMoney;
if (window.Traction.cart === undefined) window.Traction.cart = new TrCartEngine();

export const engine = window.Traction.cart;

/* ---------- hôtes ---------- */

/**
 * <tr-cart-drawer> — tiroir panier.
 * Contient un <dialog> natif (focus trap, Escape, top layer, retour du focus à l'ouvreur).
 */
class TrCartDrawer extends HTMLElement {
  #controller = null;
  #dialog = null;

  connectedCallback() {
    this.#dialog = this.querySelector('[data-ref="cartDialog"]');
    if (this.#dialog === null) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    document.addEventListener(TR_EVENTS.CART_OPEN, this.#onOpenRequest, { signal });
    document.addEventListener(TR_EVENTS.CART_CLOSE, this.#onCloseRequest, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });
    document.addEventListener('shopify:section:select', this.#onSectionSelect, { signal });
    document.addEventListener('shopify:section:deselect', this.#onSectionDeselect, { signal });

    /* Éditeur (P0-13, 11·R11) : chaque réglage recharge la section — on mémorise
       l'état ouvert au `shopify:section:unload` et on rouvre au rendu suivant. */
    if (window.Shopify?.designMode === true) {
      document.addEventListener('shopify:section:unload', this.#onSectionUnload, { signal });

      if (window.Traction?.cartDrawerReopen === true) {
        window.Traction.cartDrawerReopen = false;
        /* Après le cycle courant : le dialog doit être stylé avant showModal. */
        window.requestAnimationFrame(() => this.open());
      }
    }

    this.addEventListener('click', this.#onClick, { signal });
    this.#dialog.addEventListener('close', this.#onDialogClose, { signal });

    /* Ré-ouverture après une redirection /discount/… (bloc cart-discount). */
    if (window.sessionStorage?.getItem('tr:cart-reopen') === '1') {
      window.sessionStorage.removeItem('tr:cart-reopen');
      this.open();
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;

    if (this.#dialog?.open === true) this.#dialog.close();
    document.body.classList.remove('overflow-hidden');
  }

  /* ----- public ----- */

  get isOpen() {
    return this.#dialog?.open === true;
  }

  open() {
    if (this.#dialog === null || this.#dialog.open === true) return;

    this.#dialog.showModal();
    document.body.classList.add('overflow-hidden');
    this.classList.add('is-open');

    const focusTarget = this.querySelector('[data-ref="cartFocus"]');
    if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });

    this.dispatchEvent(new CustomEvent(TR_EVENTS.CART_DRAWER_OPEN, { bubbles: true }));
  }

  close() {
    if (this.#dialog === null || this.#dialog.open === false) return;
    this.#dialog.close();
  }

  toggle() {
    if (this.isOpen) {
      this.close();
      return;
    }

    this.open();
  }

  /* ----- interne ----- */

  #onOpenRequest = () => {
    this.open();
  };

  #onCloseRequest = () => {
    this.close();
  };

  #onDialogClose = () => {
    document.body.classList.remove('overflow-hidden');
    this.classList.remove('is-open');
    this.dispatchEvent(new CustomEvent(TR_EVENTS.CART_DRAWER_CLOSE, { bubbles: true }));
  };

  #onClick = (event) => {
    /* Clic sur le ::backdrop : la cible est le dialog lui-même. */
    if (event.target === this.#dialog) {
      this.close();
      return;
    }

    const closer = event.target.closest('[data-ref="cartClose"]');
    if (closer === null) return;

    event.preventDefault();
    this.close();
  };

  /** Délégation : tout lien vers routes.cart_url (dont #cart-icon-bubble) ouvre le tiroir. */
  #onDocumentClick = (event) => {
    if (event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

    const link = event.target.closest('a[href]');
    if (link === null || this.contains(link)) return;
    if (link.dataset.cartDrawer === 'false') return;

    if (link.id !== 'cart-icon-bubble') {
      const href = link.getAttribute('href') ?? '';
      if (href.startsWith('#') || href.startsWith('javascript:')) return;

      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        if (url.pathname.replace(/\/$/, '') !== cartPathname()) return;
        if (url.search !== '' || url.hash !== '') return;
      } catch {
        return;
      }
    }

    event.preventDefault();
    this.open();
  };

  #onSectionSelect = (event) => {
    if (event.detail?.sectionId !== this.dataset.sectionId) return;
    this.open();
  };

  #onSectionUnload = (event) => {
    if (event.detail?.sectionId !== this.dataset.sectionId) return;

    window.Traction = window.Traction ?? {};
    window.Traction.cartDrawerReopen = this.isOpen;
  };

  #onSectionDeselect = (event) => {
    if (event.detail?.sectionId !== this.dataset.sectionId) return;
    this.close();
  };
}

/**
 * <tr-cart> — page panier. L'hôte expose sa section ; « ouvrir » = focus sur le titre.
 */
class TrCart extends HTMLElement {
  open() {
    const focusTarget = this.querySelector('[data-ref="cartFocus"]');
    if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
  }

  close() {
    /* rien à fermer sur la page */
  }
}

/**
 * <tr-cart-lines> — liste des lignes : quantité +/−, saisie, suppression.
 * Chaque ligne porte `data-line-key` ; les contrôles portent `data-ref`.
 */
class TrCartLines extends HTMLElement {
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  #onClick = (event) => {
    const upsell = event.target.closest('[data-ref="lineUpsellAdd"]');
    if (upsell !== null) {
      event.preventDefault();
      this.#addUpsell(upsell);
      return;
    }

    const control = event.target.closest('[data-ref="lineIncrease"], [data-ref="lineDecrease"], [data-ref="lineRemove"]');
    if (control === null) return;

    const line = control.closest('[data-line-key]');
    if (line === null) return;

    event.preventDefault();

    const key = line.dataset.lineKey;

    if (control.dataset.ref === 'lineRemove') {
      engine.change(key, 0, { source: 'cart-line' });
      return;
    }

    const field = line.querySelector('[data-ref="lineQuantity"]');
    if (field === null) return;

    const { min, step, max } = quantityRule(field);
    const current = Math.max(0, Number(field.value) || 0);
    const increase = control.dataset.ref === 'lineIncrease';

    /* Règles de quantité (quantity_rule) : le premier palier est `min`, on
       progresse par `step`, et descendre sous `min` supprime la ligne. */
    const raised = current < min ? min : Math.min(current + step, max);
    const lowered = current - step < min ? 0 : current - step;
    const next = increase ? raised : lowered;

    if (next === current) return;
    field.value = String(next);

    engine.change(key, next, { source: 'cart-line' });
  };

  #onChange = (event) => {
    const field = event.target.closest('[data-ref="lineQuantity"]');
    if (field === null) return;

    const line = field.closest('[data-line-key]');
    if (line === null) return;

    /* Saisie libre : on cale sur la règle du produit plutôt que d'encaisser un 422. */
    const next = normalizeQuantity(field, field.value);
    if (String(next) !== field.value) field.value = String(next);

    engine.change(line.dataset.lineKey, next, { source: 'cart-line' });
  };

  /** Upsell par ligne (04·A1) : ajout 1 clic par le moteur, jamais de double envoi. */
  async #addUpsell(button) {
    const variantId = Number(button.dataset.variantId);
    if (Number.isNaN(variantId) || variantId === 0) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    /* Mode « sous chaque article » : la ligne ajoutée dit pour quel article elle est. */
    const line = { id: variantId, quantity: 1 };
    const { for: servedFor, forKey } = button.dataset;
    if (servedFor !== undefined && forKey !== undefined && forKey !== '') {
      line.properties = { [forKey]: servedFor };
    }

    const result = await engine.add([line], { source: 'cart-line-upsell' });

    if (result?.ok === false && button.isConnected) {
      button.disabled = false;
      button.setAttribute('aria-busy', 'false');
    }
  }

  #onKeydown = (event) => {
    /* Entrée dans le champ quantité : mise à jour Ajax, pas de soumission du formulaire. */
    if (event.key !== 'Enter') return;

    const field = event.target.closest('[data-ref="lineQuantity"]');
    if (field === null) return;

    event.preventDefault();
    field.dispatchEvent(new Event('change', { bubbles: true }));
  };
}

/**
 * <tr-cart-note> — note de commande, enregistrée au `change` (et debounce sur la saisie).
 */
class TrCartNote extends HTMLElement {
  #controller = null;
  #timer = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('input', this.#onInput, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    window.clearTimeout(this.#timer);
  }

  #onInput = (event) => {
    const field = event.target.closest('[data-ref="noteField"]');
    if (field === null) return;

    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.#save(field.value), 800);
  };

  #onChange = (event) => {
    const field = event.target.closest('[data-ref="noteField"]');
    if (field === null) return;

    window.clearTimeout(this.#timer);
    this.#save(field.value);
  };

  async #save(value) {
    const result = await engine.update({ note: value }, { source: 'cart-note', render: false });
    const status = this.querySelector('[data-ref="noteStatus"]');
    if (status === null) return;

    status.textContent = result?.ok === true ? (this.dataset.stringSaved ?? '') : (result?.message ?? '');
    status.hidden = status.textContent === '';
  }
}

/**
 * <tr-cart-samples> — échantillons offerts au choix (blocks/cart-samples.liquid).
 * Chaque champ est un attribut du panier (« Échantillon offert 1… »), enregistré
 * au `change` et après une pause de frappe, sans re-rendu du panier.
 */
class TrCartSamples extends HTMLElement {
  #controller = null;
  #timers = new Map();

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('input', this.#onInput, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    for (const timer of this.#timers.values()) window.clearTimeout(timer);
    this.#timers.clear();
  }

  #onInput = (event) => {
    const field = event.target.closest('[data-ref="sampleField"]');
    if (field === null) return;

    window.clearTimeout(this.#timers.get(field));
    this.#timers.set(field, window.setTimeout(() => this.#save(field), 800));
  };

  #onChange = (event) => {
    const field = event.target.closest('[data-ref="sampleField"]');
    if (field === null) return;

    window.clearTimeout(this.#timers.get(field));
    this.#save(field);
  };

  async #save(field) {
    const key = field.dataset.key;
    if (key === undefined || key === '') return;

    const result = await engine.update(
      { attributes: { [key]: field.value.trim() } },
      { source: 'cart-samples', render: false }
    );

    const status = this.querySelector('[data-ref="samplesStatus"]');
    if (status === null) return;

    status.textContent = result?.ok === true ? (this.dataset.stringSaved ?? '') : (result?.message ?? '');
    status.hidden = status.textContent === '';
  }
}

/**
 * <tr-cart-discount> — code promo appliqué EN PLACE (P1-02, 11·R3) :
 * POST `cart/update.js` { discount: 'A,B' } via le moteur (sections re-rendues,
 * pas de navigation), vérification `discount_codes[].applicable`, badges
 * retirables (re-POST sans le code). Repli sans moteur : redirection officielle
 * /discount/CODE?redirect=… (comportement historique conservé).
 */
class TrCartDiscount extends HTMLElement {
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /** Codes actuellement appliqués (rendus par Liquid dans data-applied-codes). */
  get appliedCodes() {
    try {
      const parsed = JSON.parse(this.dataset.appliedCodes ?? '[]');
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }

  #onClick = (event) => {
    const remover = event.target.closest('[data-ref="discountRemove"]');
    if (remover !== null) {
      event.preventDefault();
      this.#remove(remover.dataset.code ?? '');
      return;
    }

    if (event.target.closest('[data-ref="discountApply"]') === null) return;

    event.preventDefault();
    this.#apply();
  };

  #onKeydown = (event) => {
    if (event.key !== 'Enter') return;
    if (event.target.closest('[data-ref="discountField"]') === null) return;

    event.preventDefault();
    this.#apply();
  };

  async #apply() {
    const field = this.querySelector('[data-ref="discountField"]');
    const code = (field?.value ?? '').trim();

    if (code === '') {
      field?.focus();
      return;
    }

    if (window.Traction?.cart === undefined) {
      this.#redirect(code);
      return;
    }

    const codes = this.appliedCodes;
    const upper = code.toUpperCase();
    if (codes.some((existing) => existing.toUpperCase() === upper)) {
      if (field !== null) field.value = '';
      return;
    }

    this.#setBusy(true);
    const result = await engine.update(
      { discount: [...codes, code].join(',') },
      { source: 'cart-discount' }
    );

    /* Après le re-rendu Section Rendering, `this` peut être un nœud remplacé :
       tout retour visuel passe par l'instance fraîche. */
    if (result?.ok !== true) {
      this.#report(this.#freshHost()?.dataset.stringError ?? '', code);
      return;
    }

    const entry = Array.isArray(result.cart?.discount_codes)
      ? result.cart.discount_codes.find((item) => String(item.code).toUpperCase() === upper)
      : undefined;

    if (entry !== undefined && entry.applicable === false) {
      /* Code refusé : on le retire du panier et on l'explique, sans navigation. */
      await engine.update({ discount: codes.join(',') }, { source: 'cart-discount' });
      this.#report(this.#freshHost()?.dataset.stringNotApplicable ?? '', code);
    }
  }

  async #remove(code) {
    if (code === '') return;
    if (window.Traction?.cart === undefined) return;

    const upper = code.toUpperCase();
    const remaining = this.appliedCodes.filter((existing) => existing.toUpperCase() !== upper);

    this.#setBusy(true);
    await engine.update({ discount: remaining.join(',') }, { source: 'cart-discount' });
  }

  /** Instance re-rendue du même bloc (après remplacement de zone). */
  #freshHost() {
    const blockId = this.dataset.blockId ?? '';
    return document.querySelector(`tr-cart-discount[data-block-id="${CSS.escape(blockId)}"]`) ?? this;
  }

  #report(message, code) {
    const host = this.#freshHost();
    if (host === null) return;

    host.classList.remove('is-loading');

    const error = host.querySelector('[data-ref="discountError"]');
    if (error !== null) {
      error.textContent = message;
      error.hidden = message === '';
    }

    const field = host.querySelector('[data-ref="discountField"]');
    if (field !== null && code !== undefined) {
      field.value = code;
      field.setAttribute('aria-invalid', 'true');
      field.focus({ preventScroll: true });
    }
  }

  #setBusy(busy) {
    this.classList.toggle('is-loading', busy);

    const button = this.querySelector('[data-ref="discountApply"]');
    if (button !== null) {
      button.disabled = busy;
      button.setAttribute('aria-busy', String(busy));
    }
  }

  /** Repli historique quand aucun moteur n'est chargé. */
  #redirect(code) {
    const root = window.Shopify?.routes?.root ?? '/';
    const url = new URL(`${root}discount/${encodeURIComponent(code)}`, window.location.origin);
    url.searchParams.set('redirect', `${window.location.pathname}${window.location.search}`);

    if (this.closest('tr-cart-drawer') !== null) {
      window.sessionStorage?.setItem('tr:cart-reopen', '1');
    }

    window.location.assign(url.toString());
  }
}

/**
 * <tr-cart-upsell> — recommandations (Section Rendering sur product_recommendations_url)
 * + ajout 1 clic via le moteur. Le mode « liste de produits » est rendu en Liquid.
 */
const upsellCache = new Map();

class TrCartUpsell extends HTMLElement {
  #controller = null;
  #observer = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });

    if (this.dataset.source === 'recommendations' && this.dataset.loaded !== 'true') {
      this.#observer = new IntersectionObserver(this.#onIntersect, { rootMargin: '200px' });
      this.#observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #onIntersect = (entries) => {
    if (entries.some((entry) => entry.isIntersecting) === false) return;

    this.#observer?.disconnect();
    this.#observer = null;
    this.#load();
  };

  async #load() {
    const base = this.dataset.url;
    const sectionId = this.dataset.sectionId;
    const productId = this.dataset.productId;
    const blockId = this.dataset.blockId ?? '';

    if (base === undefined || sectionId === undefined || productId === undefined || productId === '') {
      this.#reveal(false);
      return;
    }

    const url = new URL(base, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', this.dataset.limit ?? '4');
    url.searchParams.set('intent', this.dataset.intent ?? 'complementary');

    const cacheKey = `${url.toString()}|${blockId}`;
    const list = this.querySelector('[data-ref="upsellItems"]');

    try {
      /* W-E (06·L11) : `response.ok` vérifié — sans lui une 5xx était mise en cache
         sous forme de page d'erreur. Le fetch suit le cycle de vie de l'élément
         (signal) pour ne pas écrire dans un noeud détaché. */
      let html = upsellCache.get(cacheKey);

      if (html === undefined) {
        const response = await window.fetch(url.toString(), { signal: this.#controller?.signal });
        if (response.ok === false) throw new Error(`Section rendering: ${response.status}`);
        html = await response.text();
        upsellCache.set(cacheKey, html);
      }

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector(`[data-block-id="${CSS.escape(blockId)}"] [data-ref="upsellItems"]`);

      if (fresh === null || list === null || fresh.children.length === 0) {
        this.#reveal(false);
        return;
      }

      /* Re-filtrage client (07·R10) : le fragment fetché a été rendu avec le
         panier d'alors — on retire les produits déjà présents au panier courant. */
      if (this.dataset.hideInCart === 'true') {
        const inCart = new Set(
          Array.from(document.querySelectorAll('[data-line-key][data-product-id]')).map(
            (line) => line.dataset.productId
          )
        );

        for (const item of Array.from(fresh.querySelectorAll('[data-product-id]'))) {
          if (inCart.has(item.dataset.productId)) item.remove();
        }

        if (fresh.children.length === 0) {
          this.#reveal(false);
          return;
        }
      }

      list.replaceChildren(...Array.from(fresh.childNodes));
      this.#reveal(true);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.#reveal(false);
    }
  }

  #reveal(hasItems) {
    this.hidden = hasItems === false;
    this.dataset.loaded = 'true';
  }

  #onClick = async (event) => {
    const button = event.target.closest('[data-ref="upsellAdd"]');
    if (button === null) return;

    event.preventDefault();

    const variantId = Number(button.dataset.variantId);
    if (Number.isNaN(variantId) || variantId === 0) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const result = await engine.add([{ id: variantId, quantity: 1 }], { source: 'cart-upsell' });

    if (result?.ok === false && button.isConnected) {
      button.disabled = false;
      button.setAttribute('aria-busy', 'false');
    }
  };
}

/* ---------- définitions idempotentes ---------- */

const DEFINITIONS = [
  ['tr-cart-drawer', TrCartDrawer],
  ['tr-cart', TrCart],
  ['tr-cart-lines', TrCartLines],
  ['tr-cart-note', TrCartNote],
  ['tr-cart-samples', TrCartSamples],
  ['tr-cart-discount', TrCartDiscount],
  ['tr-cart-upsell', TrCartUpsell],
];

for (const [name, constructor] of DEFINITIONS) {
  if (customElements.get(name) === undefined) customElements.define(name, constructor);
}
